import { parseAppPath } from '@/routes';

const STORAGE_KEY = 'beatly.omni.lastCampaignPath';
const STORAGE_CAMPAIGN_ID = 'beatly.omni.lastCampaignId';
const STORAGE_DISPLAY_NAME = 'beatly.omni.lastCampaignDisplayName';

/** Fired when persisted last-campaign path or display name changes (App syncs sidebar label). */
export const LAST_CAMPAIGN_NAV_SYNC_EVENT = 'beatly-last-campaign-nav-sync';

function dispatchLastCampaignNavSync(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(LAST_CAMPAIGN_NAV_SYNC_EVENT));
}

function clearStoredCampaignMeta(): void {
  try {
    sessionStorage.removeItem(STORAGE_CAMPAIGN_ID);
    sessionStorage.removeItem(STORAGE_DISPLAY_NAME);
  } catch {
    // ignore
  }
}

/** Persist campaign detail URL (tab + optional beat) for “Last campaign” nav. */
export function shouldPersistCampaignPath(pathname: string): boolean {
  const p = parseAppPath(pathname);
  if (p.currentPage === 'see-campaigns' && p.campaignId) return true;
  return false;
}

export function persistLastCampaignPathIfNeeded(pathname: string): void {
  if (!shouldPersistCampaignPath(pathname)) return;
  try {
    const { campaignId } = parseAppPath(pathname);
    const prevId = sessionStorage.getItem(STORAGE_CAMPAIGN_ID);
    if (campaignId && prevId && campaignId !== prevId) {
      clearStoredCampaignMeta();
    }
    sessionStorage.setItem(STORAGE_KEY, pathname);
    if (campaignId) {
      sessionStorage.setItem(STORAGE_CAMPAIGN_ID, campaignId);
    }
    dispatchLastCampaignNavSync();
  } catch {
    // Quota or private mode — ignore
  }
}

/**
 * Store campaign title for the sidebar (after API load). Must match persisted path’s campaign id.
 */
export function persistLastCampaignDisplayName(name: string): void {
  const trimmed = name.trim();
  if (!trimmed) return;
  try {
    sessionStorage.setItem(STORAGE_DISPLAY_NAME, trimmed);
    dispatchLastCampaignNavSync();
  } catch {
    // ignore
  }
}

/** Returns stored path if still valid, otherwise clears storage and returns null. */
export function readLastCampaignPath(): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw || !raw.startsWith('/campaigns/')) return null;
    if (!shouldPersistCampaignPath(raw)) {
      sessionStorage.removeItem(STORAGE_KEY);
      clearStoredCampaignMeta();
      dispatchLastCampaignNavSync();
      return null;
    }
    return raw;
  } catch {
    return null;
  }
}

export function readLastCampaignDisplayName(): string | null {
  try {
    const n = sessionStorage.getItem(STORAGE_DISPLAY_NAME)?.trim();
    return n || null;
  } catch {
    return null;
  }
}
